import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { API } from "../api";

const JOURS = [
  { val: 1, label: "Lundi" },
  { val: 2, label: "Mardi" },
  { val: 3, label: "Mercredi" },
  { val: 4, label: "Jeudi" },
  { val: 5, label: "Vendredi" },
];

const toStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function Absences() {
  const { token, user } = useAuth();
  const today = new Date();
  const finAnnee = `${today.getFullYear()}-12-31`;

  const [absences, setAbsences] = useState([]);
  const [selectedJours, setSelectedJours] = useState([]);
  const [debut, setDebut] = useState(toStr(today));
  const [fin, setFin] = useState(finAnnee);
  const [periode, setPeriode] = useState("entiere");
  const [ponctuel, setPonctuel] = useState("");
  const [ponctuelPeriode, setPonctuelPeriode] = useState("entiere");
  const [notif, setNotif] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(API + "/leaves/mine", { headers: { Authorization: "Bearer " + token } })
      .then(r => {
        const todayStr = toStr(new Date());
        const abs = r.data
          .filter(l => l.type === "Absence" && l.date_debut.substring(0, 10) >= todayStr)
          .sort((a, b) => a.date_debut.localeCompare(b.date_debut));
        setAbsences(abs);
      });
  };

  useEffect(() => { load(); }, [token]);

  const showNotif = (msg) => { setNotif(msg); setTimeout(() => setNotif(""), 5000); };

  const toggleJour = (val) =>
    setSelectedJours(s => s.includes(val) ? s.filter(v => v !== val) : [...s, val]);

  const buildDates = () => {
    const dates = [];
    const start = new Date(debut + "T12:00:00");
    const end = new Date(fin + "T12:00:00");
    let cur = new Date(start);
    while (cur <= end) {
      if (selectedJours.includes(cur.getDay())) dates.push(toStr(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return dates;
  };

  const generer = async () => {
    if (busy) return;
    if (selectedJours.length === 0) { showNotif("⚠️ Sélectionnez au moins un jour de la semaine."); return; }
    const dates = buildDates();
    if (dates.length === 0) { showNotif("⚠️ Aucune date dans la période choisie."); return; }
    setBusy(true);
    try {
      const r = await axios.post(API + "/leaves/absences/bulk",
        { user_id: user.id, dates, periode },
        { headers: { Authorization: "Bearer " + token } }
      );
      showNotif(`✅ ${r.data.created} jour(s) ajouté(s)${r.data.skipped ? `, ${r.data.skipped} déjà présent(s)` : ""}.`);
      load();
    } catch (e) { showNotif("❌ Erreur lors de la génération."); }
    setBusy(false);
  };

  const ajouterPonctuel = async () => {
    if (!ponctuel) return;
    setBusy(true);
    try {
      const r = await axios.post(API + "/leaves/absences/bulk",
        { user_id: user.id, dates: [ponctuel], periode: ponctuelPeriode },
        { headers: { Authorization: "Bearer " + token } }
      );
      showNotif(r.data.created ? "✅ Jour ajouté." : "ℹ️ Ce jour est déjà déclaré.");
      setPonctuel("");
      load();
    } catch (e) { showNotif("❌ Erreur."); }
    setBusy(false);
  };

  const supprimer = async (id) => {
    if (!window.confirm("Retirer ce jour non travaillé ?")) return;
    try {
      await axios.delete(API + "/leaves/" + id, { headers: { Authorization: "Bearer " + token } });
      load();
    } catch (e) { showNotif("❌ Erreur lors de la suppression."); }
  };

  const card = { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, padding: 20, marginBottom: 16 };
  const cardTitle = { fontSize: 14, fontWeight: 700, color: "#333", marginBottom: 14 };

  const OPTIONS = [
    { val: "entiere", label: "Journée entière" },
    { val: "matin", label: "Matin (½ j.)" },
    { val: "apres-midi", label: "Après-midi (½ j.)" },
  ];
  const periodeSelector = (value, setter) => (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {OPTIONS.map(o => (
        <button key={o.val} onClick={() => setter(o.val)}
          style={{
            padding: "6px 14px", borderRadius: 20, border: "1px solid", fontSize: 12, cursor: "pointer",
            fontWeight: value === o.val ? 700 : 400,
            borderColor: value === o.val ? "#7C6FE0" : "#ddd",
            background: value === o.val ? "#EEEDFE" : "#fff",
            color: value === o.val ? "#7C6FE0" : "#555",
          }}>
          {o.label}
        </button>
      ))}
    </div>
  );

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ marginBottom: 6, fontWeight: 700, fontSize: 18 }}>🗓️ Mes jours non travaillés</h2>
      <p style={{ color: "#888", fontSize: 13, marginTop: 0, marginBottom: 20 }}>
        Déclarez vos jours d'absence récurrents (temps partiel). Ils apparaissent sur les plannings, sans décompte de congés. Chaque jour reste modifiable individuellement.
      </p>

      {notif && (
        <div style={{ background: "#EAF3DE", color: "#3B6D11", border: "1px solid #97C459", padding: "10px 16px", borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 500 }}>
          {notif}
        </div>
      )}

      {/* Récurrence */}
      <div style={card}>
        <div style={cardTitle}>Récurrence hebdomadaire</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          {JOURS.map(j => (
            <button key={j.val} onClick={() => toggleJour(j.val)}
              style={{
                padding: "7px 16px", borderRadius: 20, border: "1.5px solid", fontSize: 13, cursor: "pointer",
                fontWeight: selectedJours.includes(j.val) ? 700 : 400,
                borderColor: selectedJours.includes(j.val) ? "#e0b948" : "#ddd",
                background: selectedJours.includes(j.val) ? "#F5D279" : "#fff",
                color: selectedJours.includes(j.val) ? "#7a5e00" : "#555",
              }}>
              {j.label}
            </button>
          ))}
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 6 }}>Période (s'applique aux jours cochés)</label>
          {periodeSelector(periode, setPeriode)}
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 4 }}>Du</label>
            <input type="date" value={debut} onChange={e => setDebut(e.target.value)}
              style={{ padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 4 }}>Au</label>
            <input type="date" value={fin} onChange={e => setFin(e.target.value)}
              style={{ padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13 }} />
          </div>
          <button onClick={generer} disabled={busy}
            style={{ background: busy ? "#ccc" : "#7C6FE0", color: "#fff", border: "none", borderRadius: 6, padding: "9px 22px", fontSize: 13, fontWeight: 600, cursor: busy ? "default" : "pointer" }}>
            Générer
          </button>
        </div>
      </div>

      {/* Ajout ponctuel */}
      <div style={card}>
        <div style={cardTitle}>Ajouter un jour ponctuel</div>
        <div style={{ marginBottom: 12 }}>{periodeSelector(ponctuelPeriode, setPonctuelPeriode)}</div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <input type="date" value={ponctuel} onChange={e => setPonctuel(e.target.value)}
            style={{ padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13 }} />
          <button onClick={ajouterPonctuel} disabled={busy || !ponctuel}
            style={{ background: (busy || !ponctuel) ? "#ccc" : "#7C6FE0", color: "#fff", border: "none", borderRadius: 6, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: (busy || !ponctuel) ? "default" : "pointer" }}>
            Ajouter
          </button>
        </div>
      </div>

      {/* Liste à venir */}
      <div style={card}>
        <div style={cardTitle}>Jours non travaillés à venir ({absences.length})</div>
        {absences.length === 0 ? (
          <div style={{ color: "#aaa", fontSize: 13 }}>Aucun jour déclaré pour le moment.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {absences.map(a => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#FBF3DD", border: "1px solid #f0e2bd", borderRadius: 8 }}>
                <span style={{ fontSize: 13, color: "#444" }}>
                  <span style={{ textTransform: "capitalize" }}>
                    {new Date(a.date_debut.substring(0, 10) + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
                  </span>
                  {a.periode && a.periode !== "entiere" && (
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: "#7a5e00", background: "#F5D279", padding: "2px 8px", borderRadius: 10 }}>
                      {a.periode === "matin" ? "Matin ½j" : "Après-midi ½j"}
                    </span>
                  )}
                </span>
                <button onClick={() => supprimer(a.id)}
                  style={{ background: "#FCEBEB", color: "#A32D2D", border: "1px solid #F09595", borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
                  Retirer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Absences;
